<script setup lang="ts">
import { ref, computed } from 'vue'
import QrLoginForm from '@/components/QrLoginForm.vue'
import logoUrl from '@/assets/logo.png'

const mode = ref<'qr' | '2fa'>('qr')
const email = ref<string | undefined>()

const handleModeChange = (payload: { mode: 'qr' | '2fa'; email?: string }) => {
  mode.value = payload.mode
  email.value = payload.email
}

const title = computed(() => (mode.value === '2fa' ? "Verify it's you" : 'Welcome back'))
</script>

<template>
  <div class="auth-container">
    <div class="auth-backdrop" aria-hidden="true">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
    </div>

    <div class="auth-card">
      <header class="auth-header">
        <img v-if="mode === 'qr'" :src="logoUrl" alt="Koll" class="auth-logo" />
        <div v-else class="auth-shield" aria-hidden="true">
          <i class="pi pi-shield"></i>
        </div>
        <h1 class="auth-title">{{ title }}</h1>
        <template v-if="mode === '2fa'">
          <p class="auth-subtitle">Enter the 6-digit code sent to</p>
          <p v-if="email" class="auth-email">{{ email }}</p>
        </template>
        <p v-else class="auth-subtitle">Scan the QR code with the Koll app to sign in.</p>
      </header>

      <div class="auth-body">
        <QrLoginForm @mode-change="handleModeChange" />
      </div>

      <footer v-if="mode === 'qr'" class="auth-footer">
        <span>Don't have the Koll app yet?</span>
        <a href="https://kollapp.com" target="_blank" rel="noopener">Learn more</a>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.auth-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 64px);
  padding: 2rem 1.5rem;
  overflow: hidden;
}

.auth-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.35;
}

.blob-1 {
  width: 420px;
  height: 420px;
  top: -140px;
  left: -120px;
  background: radial-gradient(circle, var(--primary-color, #673ab7), transparent 70%);
}

.blob-2 {
  width: 520px;
  height: 520px;
  bottom: -200px;
  right: -160px;
  background: radial-gradient(circle, #4f8cff, transparent 70%);
  opacity: 0.28;
}

.auth-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  padding: 2.5rem 2rem 1.75rem;
  background: var(--p-content-background, #ffffff);
  border: 1px solid var(--p-content-border-color, rgba(0, 0, 0, 0.06));
  border-radius: 20px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 12px 40px rgba(16, 24, 40, 0.08);
}

.auth-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 1.75rem;
}

.auth-logo {
  height: 44px;
  width: auto;
  margin-bottom: 1.25rem;
}

.auth-shield {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  background: var(--primary-color-transparent, rgba(103, 58, 183, 0.12));
  color: var(--primary-color, #673ab7);
  box-shadow: 0 0 0 6px var(--primary-color-transparent, rgba(103, 58, 183, 0.06));
}

.auth-shield i {
  font-size: 1.5rem;
}

.auth-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--p-text-color);
  margin: 0 0 0.375rem;
  letter-spacing: -0.01em;
}

.auth-subtitle {
  font-size: 0.925rem;
  color: var(--p-text-muted-color);
  margin: 0;
  max-width: 32ch;
}

.auth-email {
  margin: 0.4rem 0 0;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: var(--primary-color-transparent, rgba(103, 58, 183, 0.08));
  color: var(--p-text-color);
  font-size: 0.85rem;
  font-weight: 500;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-all;
  line-height: 1.35;
  text-align: center;
}

.auth-body {
  display: flex;
  justify-content: center;
}

.auth-footer {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--p-content-border-color, rgba(0, 0, 0, 0.06));
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 0.825rem;
  color: var(--p-text-muted-color);
}

.auth-footer a {
  padding: 0;
  color: var(--primary-color, #673ab7);
  font-weight: 500;
  text-decoration: none;
}

.auth-footer a:hover {
  background-color: transparent;
  text-decoration: underline;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 2rem 1.25rem 1.5rem;
    border-radius: 16px;
  }

  .auth-title {
    font-size: 1.35rem;
  }
}
</style>
